import { useState } from 'react'
import { useMemo } from 'react'
import { useAuth } from '@/modules/auth'
import { SubscriptionsApi } from '@/shared/api/subscriptions'
import { Button } from '@/ui/components'
import { Sparkles, Check } from '@/ui/icons'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/ui/components/primitives/dialog'

interface UpgradeModalProps {
  open: boolean
  onClose: () => void
}

const FEATURES = [
  'Unlimited crew members per trip',
  'Unlimited photo storage (5 GB)',
  'AI Ranger assistant',
  'Dispersed campsite finder',
  'Campspotter weather',
  'GPX export',
  'Per-link sharing permissions',
  '3D route tours',
]

export function UpgradeModal({ open, onClose }: UpgradeModalProps) {
  const { getAccessToken } = useAuth()
  const api = useMemo(() => new SubscriptionsApi(getAccessToken), [getAccessToken])

  const [interval, setInterval] = useState<'annual' | 'monthly'>('annual')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleCheckout = async () => {
    setLoading(true)
    setError(null)
    try {
      const { url } = await api.createCheckoutSession(interval)
      window.location.href = url
    } catch (err) {
      setError((err as Error).message ?? 'Something went wrong. Please try again.')
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose() }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-adventure-600" />
            Upgrade to Pro
          </DialogTitle>
          <DialogDescription>
            14-day free trial — no card required to start.
          </DialogDescription>
        </DialogHeader>

        {/* Interval toggle */}
        <div className="flex rounded-lg border p-1 gap-1 mt-2">
          <button
            type="button"
            onClick={() => setInterval('annual')}
            className={`flex-1 py-1.5 text-sm rounded-md font-medium transition-colors ${
              interval === 'annual'
                ? 'bg-adventure-600 text-white'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Annual <span className="text-xs opacity-75">$39.99/yr</span>
          </button>
          <button
            type="button"
            onClick={() => setInterval('monthly')}
            className={`flex-1 py-1.5 text-sm rounded-md font-medium transition-colors ${
              interval === 'monthly'
                ? 'bg-adventure-600 text-white'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Monthly <span className="text-xs opacity-75">$4.99/mo</span>
          </button>
        </div>

        {interval === 'annual' && (
          <p className="text-xs text-adventure-700 text-center -mt-1">
            Save ~33% vs monthly (~$3.33/mo)
          </p>
        )}

        {/* Feature list */}
        <ul className="space-y-2 py-2">
          {FEATURES.map((f) => (
            <li key={f} className="flex items-center gap-2 text-sm">
              <Check className="h-4 w-4 flex-shrink-0 text-adventure-600" />
              {f}
            </li>
          ))}
        </ul>

        {error && (
          <p className="text-sm text-red-600 rounded-md bg-red-50 px-3 py-2">{error}</p>
        )}

        <Button
          className="w-full bg-adventure-600 hover:bg-adventure-700 text-white"
          onClick={handleCheckout}
          disabled={loading}
        >
          <Sparkles className="h-4 w-4 mr-2" />
          {loading ? 'Redirecting to checkout…' : 'Start 14-Day Free Trial'}
        </Button>

        <p className="text-xs text-center text-muted-foreground">
          Cancel anytime. Billed via Stripe.
        </p>
      </DialogContent>
    </Dialog>
  )
}
